import type { SelectOption } from '../../../../components';

/**
 * Crypto-payout field rules. Which asset an owner can withdraw, and whether a
 * memo / destination tag is required, depends on the chosen network. Sending to
 * the wrong format, or omitting a required destination tag on chains that use one
 * (XRP, Stellar), can permanently lose funds, so the network drives both the asset
 * list AND the conditional memo requirement. The form renders from these helpers
 * and `billingValidation` validates from them, so they never drift.
 *
 * Address-format regexes are deliberately LENIENT (they reject obvious garbage,
 * not edge-case-valid addresses): a false rejection of a real address is worse
 * than accepting a slightly-off one, since the payout is still operator-reviewed.
 */

/** Supported payout chains. */
export type CryptoNetwork =
  | 'bitcoin'
  | 'ethereum'
  | 'tron'
  | 'bsc'
  | 'solana'
  | 'polygon'
  | 'xrp'
  | 'stellar';

/** Network options for the <Select>. Labels carry the common token-standard hint. */
export const CRYPTO_NETWORKS: SelectOption[] = [
  { value: 'bitcoin', label: 'Bitcoin' },
  { value: 'ethereum', label: 'Ethereum (ERC-20)' },
  { value: 'tron', label: 'Tron (TRC-20)' },
  { value: 'bsc', label: 'BNB Smart Chain (BEP-20)' },
  { value: 'solana', label: 'Solana' },
  { value: 'polygon', label: 'Polygon' },
  { value: 'xrp', label: 'XRP Ledger' },
  { value: 'stellar', label: 'Stellar' },
];

/** Assets withdrawable on each network (the token must exist on the chain). */
const NETWORK_ASSETS: Record<CryptoNetwork, string[]> = {
  bitcoin: ['BTC'],
  ethereum: ['ETH', 'USDT', 'USDC', 'DAI'],
  tron: ['USDT', 'USDC', 'TRX'],
  bsc: ['BNB', 'USDT', 'USDC'],
  solana: ['SOL', 'USDT', 'USDC'],
  polygon: ['MATIC', 'USDT', 'USDC'],
  xrp: ['XRP'],
  stellar: ['XLM', 'USDC'],
};

const isNetwork = (value: string): value is CryptoNetwork => value in NETWORK_ASSETS;

/** Asset options for the chosen network ([] until a known network is selected). */
export const assetsForNetwork = (network: string): SelectOption[] =>
  isNetwork(network) ? NETWORK_ASSETS[network].map((code) => ({ value: code, label: code })) : [];

/**
 * Chains whose deposits are credited by a SHARED address + a per-recipient
 * destination tag / memo. Omitting it sends funds to the chain operator's pooled
 * address with no way to attribute them, so we REQUIRE it for these. (Note: some
 * centralised-exchange deposits require a memo even on memo-less chains; that's an
 * exchange convention we can't detect, so we surface a hint instead.)
 */
const MEMO_NETWORKS: ReadonlySet<CryptoNetwork> = new Set(['xrp', 'stellar']);

/** Whether the chosen network requires a destination tag / memo. */
export const memoRequiredForNetwork = (network: string): boolean =>
  isNetwork(network) && MEMO_NETWORKS.has(network);

/** The chain-appropriate name for the memo field (for labels + errors). */
export const memoLabelForNetwork = (network: string): string =>
  network === 'xrp' ? 'Destination tag' : 'Memo';

/* ---------- address + memo validators ---------- */

const ADDRESS_PATTERNS: Record<CryptoNetwork, RegExp> = {
  // Legacy base58 (P2PKH "1" / P2SH "3") or bech32/bech32m segwit incl. Taproot
  // ("bc1"). bech32 is case-consistent: all-lowercase or all-uppercase, never mixed.
  bitcoin: /^([13][a-km-zA-HJ-NP-Z1-9]{25,39}|bc1[ac-hj-np-zA-HJ-NP-Z02-9]{11,71}|BC1[AC-HJ-NP-Z02-9]{11,71})$/,
  // EVM chains share the 0x + 40-hex format.
  ethereum: /^0x[a-fA-F0-9]{40}$/,
  bsc: /^0x[a-fA-F0-9]{40}$/,
  polygon: /^0x[a-fA-F0-9]{40}$/,
  // Tron base58check, always starts with T, 34 chars.
  tron: /^T[1-9A-HJ-NP-Za-km-z]{33}$/,
  // Solana base58 ed25519 public key, 32-44 chars.
  solana: /^[1-9A-HJ-NP-Za-km-z]{32,44}$/,
  // XRP classic address: r + base58, 25-35 chars.
  xrp: /^r[1-9A-HJ-NP-Za-km-z]{24,34}$/,
  // Stellar ed25519 public key: G + 55 base32 chars (56 total).
  stellar: /^G[A-Z2-7]{55}$/,
};

/** Validate a wallet address for the network (false for a malformed non-empty value). */
export const isValidCryptoAddress = (network: string, value: string): boolean => {
  if (!isNetwork(network)) return true; // can't validate an unknown network, so don't block
  return ADDRESS_PATTERNS[network].test(value.trim());
};

/**
 * Validate a memo / destination tag for the network. An XRP destination tag is an
 * unsigned 32-bit integer (0..4294967295). A Stellar memo's type is dictated by
 * the destination (TEXT ≤ 28 bytes, ID = uint64, or a HASH), so we only bound the
 * length generously rather than guess the type. Under-validating here is safer than
 * rejecting a valid memo the owner was given.
 */
export const isValidMemo = (network: string, value: string): boolean => {
  const memo = value.trim();
  if (network === 'xrp') {
    if (!/^\d{1,10}$/.test(memo)) return false;
    return Number(memo) <= 4294967295;
  }
  if (network === 'stellar') {
    return new TextEncoder().encode(memo).length <= 64;
  }
  return true;
};
